/**
 * 已审商品改价：TikTok Shop 统一走 Update Price（按 SKU 改展示价）。
 * OpenAPI: POST /product/202309/products/{product_id}/prices/update
 */
import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import { tiktokShopCapabilities } from '@/tiktok-platform/utils/tiktokShopCapabilities';

/** @deprecated Shein 遗留；TTS 无此枚举，保留给旧 Modal。label 为中文 key，展示时用 fixedUi。 */
export const SALE_PRICE_RISE_REASONS = [
  { code: '1', label: '商品成本上涨' },
  { code: '2', label: '物流履约费用上涨' },
  { code: '3', label: '活动结束恢复价格' },
  { code: '4', label: '其他' },
  { code: '5', label: '物流履约费用上涨（物流规则调整）' },
];

function moneyNum(v) {
  if (v == null || v === '') return null;
  const n = Number(String(v).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : null;
}

function formatMoney(v) {
  const n = moneyNum(v);
  if (n == null) return '';
  return (Math.round(n * 100) / 100).toFixed(2);
}

function skuKey(skc, sku) {
  return [
    String(skc?.skcName || skc?.id || ''),
    String(sku?.skuCode || sku?.id || sku?.supplierSku || ''),
  ].join('::');
}

function findBaselineSku(baselineForm, skc, sku) {
  const baselineSkcs = Array.isArray(baselineForm?.skcList) ? baselineForm.skcList : [];
  const skcName = String(skc?.skcName || '').trim();
  const skuCode = String(sku?.skuCode || '').trim();
  const supplierSku = String(sku?.supplierSku || '').trim();
  const baseSkc = baselineSkcs.find((row) => {
    if (skcName && String(row?.skcName || '').trim() === skcName) return true;
    return false;
  }) || baselineSkcs.find((row) => String(row?.id || '') === String(skc?.id || ''));
  if (!baseSkc) return null;
  const rows = Array.isArray(baseSkc.skuRows) ? baseSkc.skuRows : [];
  return rows.find((row) => {
    if (skuCode && String(row?.skuCode || '').trim() === skuCode) return true;
    if (supplierSku && String(row?.supplierSku || '').trim() === supplierSku) return true;
    return String(row?.id || '') === String(sku?.id || '');
  }) || null;
}

/**
 * 收集已存在 SKU（有平台 skuCode）相对 baseline 的价格变更。
 */
export function collectSkuPriceChanges(form = {}, baselineForm = null, shopTypeOrCaps = null) {
  const caps = shopTypeOrCaps && typeof shopTypeOrCaps === 'object' && 'canUpdateCost' in shopTypeOrCaps
    ? shopTypeOrCaps
    : tiktokShopCapabilities(shopTypeOrCaps || form?.shopType);
  const items = [];
  const skcList = Array.isArray(form?.skcList) ? form.skcList : [];
  skcList.forEach((skc) => {
    const skcName = String(skc?.skcName || '').trim();
    (skc?.skuRows || []).forEach((sku) => {
      const skuCode = String(sku?.skuCode || sku?.id || '').trim();
      if (!skuCode) return;
      const base = findBaselineSku(baselineForm, skc, sku);
      const nextPrice = moneyNum(sku?.price);
      const prevPrice = moneyNum(base?.price);
      if (nextPrice == null || nextPrice <= 0) return;
      if (prevPrice != null && Math.abs(nextPrice - prevPrice) < 1e-9) return;
      const increased = prevPrice != null && nextPrice > prevPrice;
      items.push({
        key: skuKey(skc, sku),
        spuName: String(form?.spuName || form?.productId || '').trim(),
        productId: String(form?.productId || form?.spuName || '').trim(),
        skcName,
        skuCode,
        currency: String(sku?.currency || form?.currency || '').trim().toUpperCase(),
        price: nextPrice,
        prevPrice,
        increased,
        specialPrice: sku?.specialPrice != null && sku.specialPrice !== ''
          ? moneyNum(sku.specialPrice)
          : (base?.specialPrice != null && base.specialPrice !== '' ? moneyNum(base.specialPrice) : null),
        colorLabel: skc?.colorLabel || '',
        sizeLabel: sku?.sizeLabel || '',
      });
    });
  });

  const mode = caps.canUpdateSalePrice ? 'sale' : (caps.canUpdateCost ? 'cost' : 'none');
  return {
    mode,
    caps,
    items,
    empty: items.length === 0,
    hasIncrease: items.some((row) => row.increased),
    skcNames: [...new Set(items.map((row) => row.skcName).filter(Boolean))],
  };
}

/** @deprecated Shein 活动锁枚举；TTS 用 `activity_commands=IMMUTABLE` 判定，见 extractTiktokPromotionLocks */
export function isPriceRiseBlockedByLock(priceLockType) {
  const n = Number(priceLockType);
  return n === 1;
}

/**
 * 归一化后端 `/publish/price-locks` 返回的 TTS 活动锁。
 * 口径：`activity_commands` 含 `IMMUTABLE` 的活动不可编辑/停用 → 覆盖商品售价被锁。
 * @returns {{ shopWide: boolean, productIds: Set<string>, skuIds: Set<string>, titles: string[] }}
 */
export function extractTiktokPromotionLocks(lockRes) {
  const root = lockRes && typeof lockRes === 'object' ? lockRes : {};
  const rows = Array.isArray(root.locks) ? root.locks : [];
  const out = {
    shopWide: false,
    productIds: new Set(),
    skuIds: new Set(),
    titles: [],
    truncated: root.truncated === true,
  };
  rows.forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const immutable = row.immutable === true || row.immutable === 'true';
    if (!immutable) return;
    if (row.shopWide === true || String(row.productLevel || '').toUpperCase() === 'SHOP') {
      out.shopWide = true;
    }
    (Array.isArray(row.productIds) ? row.productIds : []).forEach((id) => {
      const text = String(id ?? '').trim();
      if (text) out.productIds.add(text);
    });
    (Array.isArray(row.skuIds) ? row.skuIds : []).forEach((id) => {
      const text = String(id ?? '').trim();
      if (text) out.skuIds.add(text);
    });
    const title = String(row.title || '').trim();
    if (title) out.titles.push(title);
  });
  return out;
}

/**
 * 涨价是否撞上平台活动锁（TTS Promotion `IMMUTABLE`）。
 * 命中范围：全店活动 / 该商品被活动覆盖 / 涨价的 SKU 被活动覆盖。
 */
export function validateTiktokPriceChangesAgainstLocks(changes, lockRes) {
  if (!changes?.hasIncrease) {
    return { ok: true };
  }
  const locks = extractTiktokPromotionLocks(lockRes);
  if (!locks.shopWide && locks.productIds.size === 0 && locks.skuIds.size === 0) {
    return { ok: true };
  }
  const blocked = [];
  (changes.items || []).forEach((item) => {
    if (!item?.increased) return;
    const productId = String(item.productId || item.spuName || '').trim();
    const skuId = String(item.skuCode || '').trim();
    const hitsShop = locks.shopWide;
    const hitsProduct = productId ? locks.productIds.has(productId) : false;
    const hitsSku = skuId ? locks.skuIds.has(skuId) : false;
    if (!hitsShop && !hitsProduct && !hitsSku) return;
    blocked.push(`${item.skcName || item.colorLabel || productId || 'SKU'}${skuId ? ` / ${skuId}` : ''}`);
  });
  if (!blocked.length) {
    return { ok: true, warning: locks.truncated ? fixedUi('平台活动较多，本次仅核验了部分活动') : '' };
  }
  const sep = fixedUi('、');
  const activityHint = locks.titles.length
    ? fixedUiTpl('（活动：{0}）', locks.titles.slice(0, 3).join(sep))
    : '';
  const blockedText = `${blocked.slice(0, 6).join(sep)}${blocked.length > 6 ? '…' : ''}`;
  return {
    ok: false,
    truncated: locks.truncated,
    message: fixedUiTpl(
      '以下 SKU 处于平台营销活动价格锁定，不可涨价{0}：{1}。请先在 Seller Center 结束活动或等待活动结束。',
      activityHint,
      blockedText,
    ),
  };
}

/**
 * 组装 TTS Update Price 请求体。
 * @returns {{ productId: string, skus: Array<{ id: string, price: { amount: string, currency: string } }> }}
 */
export function buildTiktokUpdatePricePayload(productId, changes) {
  const pid = String(productId || changes?.items?.[0]?.productId || changes?.items?.[0]?.spuName || '').trim();
  if (!pid) throw new Error(fixedUi('缺少商品 ID，无法 Update Price'));
  if (!changes?.items?.length) throw new Error(fixedUi('没有价格变更'));
  const skus = changes.items.map((item) => {
    const skuId = String(item.skuCode || '').trim();
    if (!skuId) throw new Error(fixedUi('SKU 缺少平台 sku id'));
    const currency = String(item.currency || '').trim().toUpperCase();
    if (!currency) throw new Error(fixedUiTpl('SKU {0} 缺少币种', skuId));
    return {
      id: skuId,
      price: {
        amount: formatMoney(item.price),
        currency,
      },
    };
  });
  return { productId: pid, skus };
}

/** @deprecated 兼容旧调用 */
export function buildSaveSalePricePayload(changes, options = {}) {
  const productId = options.productId || changes?.items?.[0]?.productId || changes?.items?.[0]?.spuName || '';
  return buildTiktokUpdatePricePayload(productId, changes);
}

/** @deprecated Shein update-cost；TTS 不再使用 */
export function buildUpdateCostPayload() {
  throw new Error(fixedUi('TikTok Shop 不支持 update-cost，请使用 Update Price'));
}

export function isEmptyPartialEditError(err) {
  const msg = String(err?.message || err || '');
  return /没有可提交的变更/.test(msg);
}

export function interpretPriceUpdateResponse(res, mode = 'sale') {
  if (!res || typeof res !== 'object') {
    return { ok: false, message: fixedUi('改价接口无响应') };
  }
  const code = res.code == null ? '' : String(res.code);
  const gatewayOk = code === '0' || code === '000000' || code === '';
  if (!gatewayOk) {
    return {
      ok: false,
      message: res.msg || res.message || fixedUi('价格更新失败'),
    };
  }
  if (mode === 'sale') {
    return { ok: true, pending: false, message: fixedUi('售价已更新') };
  }
  return { ok: true, pending: false, message: fixedUi('价格已更新') };
}

/** @deprecated */
export function chunkUpdateCostPayload(payload) {
  return [payload];
}
